// Canonical prijs/btw-berekening. Alle prijzen worden ex btw opgeslagen
// (bron van waarheid) en op precies één plek omgerekend. Alle afrondingen
// gebeuren via `Math.round`. De invariant is overal:
//
//     incl = ex + vat        met     vat = Math.round(ex * VAT_RATE)
//
// Door voor optellingen éérst de kleine bedragen te sommeren en pas daarna
// af te ronden, kan er nergens een centverschil ontstaan tussen regel-
// totalen en subtotalen die tegelijk in beeld staan.

import { applyDiscount } from "@/lib/catalog-format";

export const VAT_RATE = 0.21;

export type PricedLine = { price_cents: number; qty: number };

export type LineTotals = {
  unitEx: number;
  unitVat: number;
  unitIncl: number;
  unitDisplay: number;
  lineEx: number;
  lineVat: number;
  lineIncl: number;
  lineDisplay: number;
};

export type CartTotals = {
  lines: LineTotals[];
  subtotalEx: number;
  subtotalVat: number;
  subtotalIncl: number;
  subtotalDisplay: number;
  vatAmount: number;
};

/** VAT op een bedrag ex btw. Enige plek waar `* VAT_RATE` staat. */
export function vatOf(ex: number): number {
  return Math.round(ex * VAT_RATE);
}

/** Bedrag ex btw → incl btw via `ex + vat` (nooit `* 1.21`). */
export function toIncl(ex: number): number {
  return ex + vatOf(ex);
}

/**
 * Bereken alle totalen voor een reeks regels in één keer. Regel-totalen
 * en subtotalen komen uit dezelfde optelling, dus wat de klant ziet klopt
 * altijd op de cent.
 */
export function computeCartTotals(
  items: PricedLine[],
  discountPct: number,
  inclVat: boolean,
): CartTotals {
  let subtotalEx = 0;
  let subtotalVat = 0;

  const lines: LineTotals[] = items.map((it) => {
    const unitEx = applyDiscount(it.price_cents, discountPct);
    const unitVat = vatOf(unitEx);
    const unitIncl = unitEx + unitVat;

    const lineEx = unitEx * it.qty;
    const lineVat = vatOf(lineEx);
    const lineIncl = lineEx + lineVat;

    subtotalEx += lineEx;
    subtotalVat += lineVat;

    return {
      unitEx,
      unitVat,
      unitIncl,
      unitDisplay: inclVat ? unitIncl : unitEx,
      lineEx,
      lineVat,
      lineIncl,
      lineDisplay: inclVat ? lineIncl : lineEx,
    };
  });

  const subtotalIncl = subtotalEx + subtotalVat;
  return {
    lines,
    subtotalEx,
    subtotalVat,
    subtotalIncl,
    subtotalDisplay: inclVat ? subtotalIncl : subtotalEx,
    vatAmount: subtotalVat,
  };
}
